import React, { useRef } from 'react';
import { GovIcon } from '@gov-design-system-ce/react';
import { parseIcon } from '@gov-nx/component/common';
import { useLocale } from '@gov-nx/core/service';
import { PageCode, useRozcestnikContext } from '@gov-nx/module/page';
import { useCmsUrlPath } from '@gov-nx/store';
import {
	Chip,
	ChipNav,
	ContentLayout,
	HeaderLayout,
	Icon,
	InfoBanner,
	Pagination,
} from '@gov-nx/ui/web';
import { scrollToElement } from '@gov-nx/web';
import { FavoriteToggle } from '../../Favorite/FavoriteToggle';
import { MarkdownRender } from '../../Markdown/MarkdownRender';
import { Tile, Tiles } from '../../Tile/Tile';
import { SignpostSearch } from './SignpostSearch';
import { SignpostSkeleton } from './SignpostSkeleton';

export interface SignpostViewProps {
	code: PageCode;
}

export const SignpostView = ({ code }: SignpostViewProps) => {
	const { t } = useLocale(PageCode['rozcestnik']);
	const sectionRef = useRef(null);
	const { baseCmsUrlPath } = useCmsUrlPath();
	const {
		data,
		isSignpostDataFetching,
		groups,
		filter,
		numberOfSignposts,
		favorites,
		goToPage,
		setFilterFavourites,
		setFilterAll,
		setFilterActiveGroup,
	} = useRozcestnikContext();

	return (
		<ContentLayout>
			<HeaderLayout>
				<SignpostSearch code={code} />
			</HeaderLayout>
			<ChipNav
				moreChips={true}
				aria-label={t('filtr.kategorie')}>
				<li>
					<Chip
						color="primary"
						asChild
						size="m"
						role="tab"
						type={
							!filter.favourites && filter.serviceGroups === null
								? 'solid'
								: 'outlined'
						}
						aria-selected={!filter.favourites && filter.serviceGroups === null}
						aria-label={t('filtr.wcag.vse')}>
						<button
							onClick={() => setFilterAll()}
							type="button">
							<span>{t('filtr.vse')}</span>
						</button>
					</Chip>
				</li>
				<li>
					<Chip
						color="primary"
						asChild
						size="m"
						iconStart={
							<Icon
								type="templates"
								name={favorites.length ? 'star-fill' : 'star'}
							/>
						}
						identifier="favorites-toggle"
						type={filter.favourites ? 'solid' : 'outlined'}
						aria-selected={filter.favourites}
						aria-label={t('filtr.wcag.oblibene')}>
						<button
							onClick={() => setFilterFavourites()}
							type="button">
							<span>{t('filtr.oblibene')}</span>
						</button>
					</Chip>
				</li>
				{groups.map((group) => (
					<li key={group.label}>
						<Chip
							color="primary"
							asChild
							size="m"
							type={filter.serviceGroups === group.id ? 'solid' : 'outlined'}
							aria-selected={filter.serviceGroups === group.id}
							aria-label={group.label}>
							<button
								onClick={() => setFilterActiveGroup(group.id)}
								type="button">
								{group.label}
							</button>
						</Chip>
					</li>
				))}
			</ChipNav>
			<section ref={sectionRef}>
				{isSignpostDataFetching ? (
					<SignpostSkeleton />
				) : data.length ? (
					<Tiles>
						{data.map((signpost) => {
							if (!signpost.name) return null;
							if (!signpost.action) return null;

							return (
								<Tile
									key={signpost.id}
									{...signpost.action}
									title={signpost.name}
									icon={
										signpost.icon?.file ? (
											<img
												src={baseCmsUrlPath + signpost.icon?.file}
												alt=""
												className="gov-icon object-contain"
												data-size="5xl"
											/>
										) : (
											<GovIcon
												{...(signpost.icon?.icon
													? parseIcon(signpost.icon.icon)
													: { name: undefined, type: undefined })}
												slot="icon"
												size="5xl"
												color="primary"
											/>
										)
									}
									favoriteToggle={
										signpost.code ? (
											<FavoriteToggle
												size="s"
												code={signpost.code}
												name={signpost.name}
											/>
										) : undefined
									}>
									{signpost.description ? (
										<div className="text-(--text-secondary)">
											<MarkdownRender>{signpost.description}</MarkdownRender>
										</div>
									) : null}
								</Tile>
							);
						})}
					</Tiles>
				) : (
					<InfoBanner
						headline={t('chyba.nic-jsme-nenasli')}
						text={t('chyba.nepodarilo-se-najit-zadnou-sluzbu')}
					/>
				)}
			</section>
			{numberOfSignposts > filter.pageSize ? (
				<footer className="mt-6">
					<Pagination
						color="primary"
						size="m"
						pageSize={filter.pageSize}
						current={filter.page}
						onPage={(page) => {
							scrollToElement(sectionRef.current, 250);
							goToPage(page);
						}}
						wcag-label={t('wcag.strankovani')}
						total={numberOfSignposts}
					/>
				</footer>
			) : null}
		</ContentLayout>
	);
};
